import { useSetAtom } from 'jotai';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { pluralize } from '@flintmint/core-shared/utils';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { useRouteInfo } from '@/hooks/use-route-info';
import { useDraftDocument } from '@/hooks/use-draft-document';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { useSmartArchive, useSmartDelete, useSmartUpdate } from '@/hooks/rxdb-smart';
import { isArchiveableDocument } from '@/hooks/rxdb-smart/use-smart-archive';

import { appRoutes } from '@/constants/routes';
import { getSelectedItemType } from '@/constants/item-types';
import { multiSelectionsAtom, sidebarOverlayAtom, taggedDocsMultiSelectionsAtom } from '@/state';

import type { Note } from '@flintmint/core-shared/rxdb';
import type { NoteVariant } from '@/providers/rxdb/constants';
import type { SearchableDocument } from '@/services/search-service/orama-schema';
import type { BlockContext, DocumentWithType } from '@/constants/types';
import type { AlertProps } from '@flintmint/web-shared/components/alert';
import type { MenuItemProps } from '@flintmint/web-shared/components/menu-item';

export type OriginNoteLink = Partial<Note> & { isDraft?: boolean };

type OpenDocumentOptions = {
  openInNewTab?: boolean;
};

type DestructiveActionCallbacks = {
  onConfirm?: () => void;
  onDone?: () => void;
};

export const useItemActions = () => {
  const setSidebarOverlay = useSetAtom(sidebarOverlayAtom);

  const { tagId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateWithSearch = useNavigateWithSearch();

  const { openDraft } = useDraftDocument();
  const { isTags, isLibrary, basePath } = useRouteInfo();
  const { showToastNotification } = useToaster();
  const { delete: smartDelete } = useSmartDelete();
  const { update: smartUpdate } = useSmartUpdate();
  const { archive: smartArchive } = useSmartArchive();

  const setMultiSelections = useSetAtom(multiSelectionsAtom);
  const setTaggedDocsMultiSelections = useSetAtom(taggedDocsMultiSelectionsAtom);

  const showArchivedToast = (selections: DocumentWithType[]) => {
    const title =
      selections.length > 1 ? `${selections.length} items moved to archive` : `${selections[0]?.name ?? 'Item'} moved to archive`;

    showToastNotification({
      type: 'success',
      title,
      action: {
        label: 'Go to Archive',
        onClick: () => {
          navigate(appRoutes.archive, {
            state: basePath === appRoutes.archive ? undefined : { backToFrom: basePath },
          });
        },
      },
    });
  };

  const onCreateNewNote = (payload: { prompt: string; variant?: NoteVariant; domain?: 'medical' }) => {
    const { prompt = '', variant = 'notes', domain } = payload;
    openDraft({ type: variant, prompt, domain });
  };

  const onCreateBlankUserSource = (originNoteLink?: OriginNoteLink) => {
    openDraft({
      type: 'userSources',
      navigateOptions: originNoteLink ? { state: { originNoteLink } } : undefined,
    });
  };

  const createNew = ({
    type,
    domain,
    prompt = '',
    context,
    originNoteLink,
  }: {
    type: string;
    prompt?: string;
    domain?: 'medical';
    context?: BlockContext;
    originNoteLink?: OriginNoteLink;
  }) => {
    switch (type) {
      case 'notes':
        return onCreateNewNote({ prompt, domain });
      case 'templates':
        return onCreateNewNote({ prompt, variant: 'templates' });
      case 'userSources':
        return onCreateBlankUserSource(originNoteLink);
      case 'blocks':
        return setSidebarOverlay({ type: 'blocks', context, draftId: crypto.randomUUID() });

      default:
        return null;
    }
  };

  const navigateToEditor = (document: DocumentWithType) => {
    const type = document.type;

    switch (type) {
      case 'userSources':
        return navigate(`${appRoutes.cortex}/${document.id}`);
      case 'templates':
      case 'notes':
        return navigate(`${appRoutes.library}/${document.id}`);
      case 'blocks':
        return navigate(`${appRoutes.library}`, { state: { blockId: document.id } });
      default:
        return null;
    }
  };

  const buildSearchWithoutDraftType = (search: string) => {
    const params = new URLSearchParams(search);
    params.delete('draftType');
    params.delete('domain');
    const next = params.toString();
    return next ? `?${next}` : '';
  };

  const openUrl = (url: string, openInNewTab?: boolean, removeSearchParams: string[] = []) => {
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigateWithSearch(url, { removeSearchParams });
  };

  const openDocument = (document: DocumentWithType, options: OpenDocumentOptions = {}) => {
    const { openInNewTab } = options;

    if (document.type === 'blocks') {
      if (!isLibrary) {
        return navigateToEditor(document);
      }

      return setSidebarOverlay({
        blockId: document.id,
        type: 'blocks',
      });
    }

    // If you're in Tags view, clicking a document should open the editor flow you already have
    if (isTags && document.type !== 'tags') {
      return navigateToEditor(document);
    }

    const search = buildSearchWithoutDraftType(location.search);
    const draftSearchParams = ['draftType', 'domain'];

    switch (document.type) {
      case 'tags':
        return openUrl(`${appRoutes.tags}/${document.id}${search}`, openInNewTab, draftSearchParams);
      case 'userSources':
        return openUrl(`${appRoutes.cortex}/${document.id}${search}`, openInNewTab, draftSearchParams);
      default:
        return openUrl(`${appRoutes.library}/${document.id}${search}`, openInNewTab, draftSearchParams);
    }
  };

  const onRemoveFromTag = async (selections: SearchableDocument[]): Promise<any> => {
    for (const doc of selections) {
      await smartUpdate(doc, {
        tagIds: doc.tagIds.filter((id) => id !== tagId),
      });
    }
    setTaggedDocsMultiSelections(null);
  };

  const afterDestructiveAction = (selections: DocumentWithType[], onDone?: () => void, onClose?: () => void) => {
    const deletedSelectionId = selections[0]?.id;
    if (selections.length === 1) {
      setMultiSelections((currentSelections) => {
        if (Array.isArray(currentSelections)) {
          return currentSelections.filter((selection) => selection.id !== deletedSelectionId);
        }
        return null;
      });
    }
    onDone?.();
    onClose?.();
  };

  const archiveAlertConfig = (
    selections: DocumentWithType[],
    { onDone, onConfirm }: DestructiveActionCallbacks = {}
  ): AlertProps => {
    const labels = getArchiveActionLabels(selections);
    return {
      type: 'info',
      color: 'hero',
      icon: 'alert-01-bulk',
      message: archiveConfirmMessage(selections),
      title: buildArchiveAlertTitle(selections),
      secondaryButton: { children: 'Keep' },
      primaryButton: {
        children: labels.confirmLabel,
        color: 'hero',
        onClick: (onClose) => {
          onConfirm?.();
          Promise.resolve(smartArchive(selections))
            .then(() => showArchivedToast(selections))
            .finally(() => afterDestructiveAction(selections, onDone, onClose));
        },
      },
    };
  };

  const deleteAlertConfig = (
    selections: DocumentWithType[],
    { onDone, onConfirm }: DestructiveActionCallbacks = {}
  ): AlertProps => {
    const labels = getDeleteActionLabels(selections);
    return {
      type: 'danger',
      message: deleteConfirmMessage(selections),
      // Node title bypasses Alert's danger string prefix ("Delete …?").
      title: buildDeleteAlertTitle(selections),
      primaryButton: {
        children: labels.confirmLabel,
        onClick: (onClose) => {
          onConfirm?.();
          Promise.resolve(smartDelete(selections)).finally(() => afterDestructiveAction(selections, onDone, onClose));
        },
      },
    };
  };

  const configureArchiveOption = (selections: DocumentWithType[], callbacks?: DestructiveActionCallbacks): MenuItemProps => {
    const labels = getArchiveActionLabels(selections);
    return {
      name: labels.name,
      icon: labels.icon,
      showConfirmation: true,
      deleteData: archiveAlertConfig(selections, callbacks),
    };
  };

  const configureDeleteOption = (selections: DocumentWithType[], callbacks?: DestructiveActionCallbacks): MenuItemProps => {
    const labels = getDeleteActionLabels(selections);
    return {
      danger: true,
      name: labels.name,
      icon: labels.icon,
      showConfirmation: true,
      deleteData: deleteAlertConfig(selections, callbacks),
    };
  };

  // Archiveable types get Move to archive + Delete; tags (and any non-archiveable) get Delete only.
  // Note-scoped userSources are archiveable in the data layer but not via menus (Delete only).
  // Empty selection still offers both archiveable affordances so the mini action bar can render disabled icons.
  const configureDestructiveOptions = (
    selections: DocumentWithType[],
    callbacks?: DestructiveActionCallbacks
  ): MenuItemProps[] => {
    if (selections.length === 0 || selectionsAreArchiveable(selections)) {
      return [configureArchiveOption(selections, callbacks), configureDeleteOption(selections, callbacks)];
    }
    return [configureDeleteOption(selections, callbacks)];
  };

  const configureRemoveFromTagOption = (selections: SearchableDocument[], onRemove?: () => void): MenuItemProps => {
    const firstItemType = getSelectedItemType(selections[0]?.type ?? '');
    return {
      danger: true,
      icon: 'unlink-04',
      name: 'Remove from Tag',
      showConfirmation: true,
      deleteData: {
        type: 'danger',
        message: `Are you sure you want to remove this ${
          selections?.length > 1 ? 'items' : firstItemType
        } from this tag? This action can’t be undone.`,
        title: <>Remove From Tag</>,
        primaryButton: {
          children: 'Remove',
          onClick: async (onClose) => {
            await onRemoveFromTag(selections);
            onRemove?.();
            onClose?.();
          },
        },
      },
    };
  };

  return {
    createNew,
    openDocument,
    onRemoveFromTag,
    navigateToEditor,
    archiveAlertConfig,
    deleteAlertConfig,
    configureArchiveOption,
    configureDeleteOption,
    configureDestructiveOptions,
    configureRemoveFromTagOption,
  };
};

// Alert renders a hardcoded "Delete" prefix for string titles, so titles are passed as nodes.
const buildArchiveAlertTitle = (selections: DocumentWithType[]) => {
  const subject = selections.length > 1 ? pluralize(selections.length, 'item') : selections[0]?.name;

  return (
    <>
      Move <span>{subject}</span> to archive?
    </>
  );
};

const buildDeleteAlertTitle = (selections: DocumentWithType[]) => {
  const subject = selections.length > 1 ? pluralize(selections.length, 'item') : selections[0]?.name;

  return (
    <>
      Delete <span>{subject}</span>?
    </>
  );
};

const getArchiveActionLabels = (selections: DocumentWithType[]) => {
  const name = selections.length > 1 ? `Move ${selections.length} items to archive` : 'Move to archive';
  return { name, icon: 'archive-02' as const, confirmLabel: name };
};

const getDeleteActionLabels = (selections: DocumentWithType[]) => ({
  name: selections.length > 1 ? `Delete ${pluralize(selections.length, 'item')}` : 'Delete',
  icon: 'delete-01' as const,
  confirmLabel: selections.length > 1 ? `Delete ${selections.length}` : 'Delete',
});

const archiveConfirmMessage = (data: DocumentWithType[]) => {
  const firstItemType = getSelectedItemType(data[0]?.variant || data[0]?.type);
  return data.length > 1
    ? 'Are you sure you want to move these items to Archive?'
    : `Are you sure you want to move this ${firstItemType} to Archive?`;
};

const deleteConfirmMessage = (data: DocumentWithType[]) => {
  const firstItemType = getSelectedItemType(data[0]?.variant || data[0]?.type);
  return data.length > 1
    ? 'Are you sure you want to permanently delete these items? This action can’t be undone.'
    : `Are you sure you want to permanently delete this ${firstItemType}? This action can’t be undone.`;
};

const isMenuArchiveableDocument = (doc: DocumentWithType) => {
  if (!isArchiveableDocument(doc)) return false;
  const type = doc.variant || doc.type;
  // TODO Temporary: hide instruction archive until instructions library exists.
  if (type === 'instructions') return false;
  // Note-scoped sources stay out of Archive menus; Delete remains available.
  return !(type === 'userSources' && doc.availabilityScope === 'note');
};

const selectionsAreArchiveable = (selections: DocumentWithType[]) =>
  selections.length > 0 && selections.every(isMenuArchiveableDocument);
